@if (isOpen()) {
  <!-- Overlay backdrop -->
  <div
    class="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm transition-opacity duration-300"
    (click)="close()"
    aria-hidden="true"></div>

  <!-- Slide-over panel -->
  <aside
    role="dialog"
    aria-modal="true"
    aria-label="Reading preferences"
    class="fixed inset-y-0 right-0 z-50 flex w-80 flex-col bg-white dark:bg-slate-900 shadow-2xl border-l border-[var(--reader-border)] transform transition-transform duration-300 animate-slide-in-right">

    <!-- Header -->
    <div class="flex items-center justify-between border-b border-[var(--reader-border)] px-5 py-4">
      <div class="flex items-center gap-2">
        <svg class="h-5 w-5 text-[var(--reader-accent)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
        </svg>
        <h2 class="font-heading text-sm font-semibold text-text">Reading Preferences</h2>
      </div>
      <button
        (click)="close()"
        aria-label="Close reading preferences"
        class="rounded-lg p-1.5 text-[var(--reader-muted)] hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-text transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--reader-accent)]">
        <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <!-- Scrollable body -->
    <div class="flex-1 overflow-y-auto px-5 py-6 space-y-7">

      <!-- ── Section: Accent colour ── -->
      <section aria-labelledby="accent-heading">
        <h3 id="accent-heading" class="mb-3 font-heading text-xs font-semibold uppercase tracking-wider text-[var(--reader-muted)]">
          Accent colour
        </h3>
        <div class="flex gap-3 flex-wrap" role="radiogroup" aria-labelledby="accent-heading">
          @for (preset of accentPresets; track preset.key) {
            <button
              role="radio"
              [attr.aria-checked]="currentPrefs().accentKey === preset.key"
              [attr.aria-label]="preset.name"
              (click)="setAccent(preset.key)"
              [style.background-color]="isDark() ? preset.dark : preset.light"
              [class]="currentPrefs().accentKey === preset.key
                ? 'h-8 w-8 rounded-full ring-2 ring-offset-2 ring-offset-white dark:ring-offset-slate-900 ring-current shadow-sm transition-transform scale-110 focus-visible:outline-none focus-visible:ring-2'
                : 'h-8 w-8 rounded-full shadow-sm hover:scale-110 transition-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--reader-accent)]'">
            </button>
          }
        </div>
      </section>

      <!-- ── Section: Font size ── -->
      <section aria-labelledby="font-size-heading">
        <h3 id="font-size-heading" class="mb-3 font-heading text-xs font-semibold uppercase tracking-wider text-[var(--reader-muted)]">
          Text size
        </h3>
        <div class="flex gap-2" role="radiogroup" aria-labelledby="font-size-heading">
          @for (opt of fontSizeOptions; track opt.key) {
            <button
              role="radio"
              [attr.aria-checked]="currentPrefs().fontSize === opt.key"
              (click)="setFontSize(opt.key)"
              [class]="currentPrefs().fontSize === opt.key
                ? 'flex-1 rounded-xl border-2 border-[var(--reader-accent)] bg-[color-mix(in_srgb,var(--reader-accent)_10%,transparent)] py-2 text-center text-sm font-semibold text-[var(--reader-accent)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--reader-accent)]'
                : 'flex-1 rounded-xl border border-[var(--reader-border)] py-2 text-center text-sm text-[var(--reader-muted)] hover:border-[var(--reader-accent)] hover:text-[var(--reader-accent)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--reader-accent)]'">
              {{ opt.label }}
            </button>
          }
        </div>
      </section>

      <!-- ── Section: Content width ── -->
      <section aria-labelledby="width-heading">
        <h3 id="width-heading" class="mb-3 font-heading text-xs font-semibold uppercase tracking-wider text-[var(--reader-muted)]">
          Content width
        </h3>
        <div class="flex gap-2" role="radiogroup" aria-labelledby="width-heading">
          @for (opt of widthOptions; track opt.key) {
            <button
              role="radio"
              [attr.aria-checked]="currentPrefs().width === opt.key"
              (click)="setWidth(opt.key)"
              [class]="currentPrefs().width === opt.key
                ? 'flex-1 rounded-xl border-2 border-[var(--reader-accent)] bg-[color-mix(in_srgb,var(--reader-accent)_10%,transparent)] py-2 text-center text-sm font-semibold text-[var(--reader-accent)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--reader-accent)]'
                : 'flex-1 rounded-xl border border-[var(--reader-border)] py-2 text-center text-sm text-[var(--reader-muted)] hover:border-[var(--reader-accent)] hover:text-[var(--reader-accent)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--reader-accent)]'">
              {{ opt.label }}
            </button>
          }
        </div>
        <!-- Visual width key -->
        <div class="mt-3 flex items-end gap-1 px-1" aria-hidden="true">
          <div class="h-3 w-6 rounded-sm bg-[var(--reader-border)]"></div>
          <div class="h-3 w-10 rounded-sm bg-[var(--reader-border)]"></div>
          <div class="h-3 w-16 rounded-sm bg-[var(--reader-border)]"></div>
        </div>
      </section>

      <!-- ── Section: Theme ── -->
      <section aria-labelledby="theme-heading">
        <h3 id="theme-heading" class="mb-3 font-heading text-xs font-semibold uppercase tracking-wider text-[var(--reader-muted)]">
          Colour theme
        </h3>
        <div class="flex gap-3">
          <!-- Light button -->
          <button
            role="radio"
            [attr.aria-checked]="!isDark()"
            aria-label="Light mode"
            (click)="isDark() && toggleTheme()"
            [class]="!isDark()
              ? 'flex flex-1 items-center justify-center gap-2 rounded-xl border-2 border-[var(--reader-accent)] bg-[color-mix(in_srgb,var(--reader-accent)_10%,transparent)] py-2.5 text-sm font-semibold text-[var(--reader-accent)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--reader-accent)]'
              : 'flex flex-1 items-center justify-center gap-2 rounded-xl border border-[var(--reader-border)] py-2.5 text-sm text-[var(--reader-muted)] hover:border-[var(--reader-accent)] hover:text-[var(--reader-accent)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--reader-accent)]'">
            <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M18.364 17.657l-.707-.707M6.343 6.343l-.707-.707M12 8a4 4 0 100 8 4 4 0 000-8z"/>
            </svg>
            Light
          </button>
          <!-- Dark button -->
          <button
            role="radio"
            [attr.aria-checked]="isDark()"
            aria-label="Dark mode"
            (click)="!isDark() && toggleTheme()"
            [class]="isDark()
              ? 'flex flex-1 items-center justify-center gap-2 rounded-xl border-2 border-[var(--reader-accent)] bg-[color-mix(in_srgb,var(--reader-accent)_10%,transparent)] py-2.5 text-sm font-semibold text-[var(--reader-accent)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--reader-accent)]'
              : 'flex flex-1 items-center justify-center gap-2 rounded-xl border border-[var(--reader-border)] py-2.5 text-sm text-[var(--reader-muted)] hover:border-[var(--reader-accent)] hover:text-[var(--reader-accent)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--reader-accent)]'">
            <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"/>
            </svg>
            Dark
          </button>
        </div>
      </section>

    </div>

    <!-- Footer hint -->
    <div class="border-t border-[var(--reader-border)] px-5 py-3">
      <p class="text-center text-xs text-[var(--reader-muted)]">Preferences saved automatically</p>
    </div>

  </aside>
}
